// Mirrors impire-sdk-go/blueprint — the blueprint family's wire
// (02-DESIGN/13-blueprints/01-the-blueprint.md § subjects; decision 0037):
// the subjects a parent's admin and a workspace's admin speak, the headers,
// the envelope every reply carries inside its sealed body, the codes, the
// document's shape, the compliance report and its verification in the
// browser, and a secret sealed by the parent toward the child vault's key.
// JSON field names are the Go file's; a shape that differs is a defect here.
// A client writes the tokenless form — `blueprint.library.<name>.set`,
// `blueprint.child.<child>.assign` — and the import fills the caller's slug
// after the family.
import { headers as newHeaders } from "@nats-io/nats-core";
import { base64Encode, base64UrlDecode, utf8Decode, utf8Encode } from "./encoding.js";
import { Local, validPublicKey } from "./envelope.js";
import { audienceOf, CARRIER_HEADER, verifyAttestation, } from "./identity.js";
import { client, Family, validSlug } from "./subject.js";
/** SERVICE is the blueprint service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "blueprint";
/** The family's tokens. */
export const Token = {
    own: "own",
    child: "child",
    children: "children",
    library: "library",
};
/** The verbs. */
export const Verb = {
    set: "set",
    get: "get",
    history: "history",
    delete: "delete",
    status: "status",
    watch: "watch",
    report: "report",
    list: "list",
    assign: "assign",
};
/** MAX_LIBRARY is how many named blueprints a parent's library holds; a write past it is refused limit. */
export const MAX_LIBRARY = 32;
/** EXPECT_REVISION_HEADER on a set makes the write the bucket's compare-and-set, refused conflict when anything landed since. */
export const EXPECT_REVISION_HEADER = "Impire-Blueprint-Expect-Revision";
/** The leased watch's headers, in the configuration design's grammar. */
export const WATCH_HEADERS = {
    snapshot: "Impire-Blueprint-Snapshot",
    since: "Impire-Blueprint-Since",
    revision: "Impire-Blueprint-Revision",
};
/** The codes the service answers with. */
export const Code = {
    badRequest: "bad-request",
    conflict: "conflict",
    notFound: "not-found",
    notAllowed: "not-allowed",
    reportInvalid: "report-invalid",
    reportExpired: "report-expired",
    reportMismatch: "report-mismatch",
    /** A delete of a named blueprint a child uses. */
    inUse: "in-use",
    /** A named blueprint past MAX_LIBRARY. */
    limit: "limit",
    failed: "failed",
};
/** MAX_DOCUMENT bounds a blueprint, as a settings document is bounded. */
export const MAX_DOCUMENT = 64 * 1024;
/** ownGet is `blueprint.own.get`: the workspace's own blueprint, as its admin reads it on Settings. */
export function ownGet() {
    return client(Family.blueprint, Token.own, Verb.get);
}
/** ownWatch is `blueprint.own.watch`, the steward's. */
export function ownWatch() {
    return client(Family.blueprint, Token.own, Verb.watch);
}
/** ownReport is `blueprint.own.report`, the steward's. */
export function ownReport() {
    return client(Family.blueprint, Token.own, Verb.report);
}
/**
 * ownStatus is `blueprint.own.status`: the workspace's own latest verified
 * report, which its admin's Settings reads for the revision applied (decision 0064).
 */
export function ownStatus() {
    return client(Family.blueprint, Token.own, Verb.status);
}
/** childSubject is `blueprint.child.<child>.<verb>`, a parent's verb on one child. */
export function childSubject(child, verb) {
    if (!validSlug(child)) {
        throw new Error(`blueprint: ${JSON.stringify(child)} is not a workspace slug`);
    }
    return client(Family.blueprint, Token.child, child, verb);
}
/** assignSubject is `blueprint.child.<child>.assign`: which named blueprint a child uses, and its secrets. */
export function assignSubject(child) {
    if (!validSlug(child)) {
        throw new Error(`blueprint: ${JSON.stringify(child)} is not a workspace slug`);
    }
    return client(Family.blueprint, Token.child, child, Verb.assign);
}
/** validName reports whether a named blueprint's name is a subject token. */
export function validName(name) {
    return /^[A-Za-z0-9_-]+$/.test(name);
}
/** libraryList is `blueprint.library.list`: the caller's named blueprints. */
export function libraryList() {
    return client(Family.blueprint, Token.library, Verb.list);
}
/** librarySubject is `blueprint.library.<name>.<verb>`, a parent's verb on one of its named blueprints. */
export function librarySubject(name, verb) {
    if (!validName(name)) {
        throw new Error(`blueprint: ${JSON.stringify(name)} is not a blueprint name`);
    }
    return client(Family.blueprint, Token.library, name, verb);
}
/** childrenSubject is `blueprint.children`: every child the relation gives the caller. */
export function childrenSubject() {
    return client(Family.blueprint, Token.children);
}
/** The two operations of a revision. */
export const EntryOp = { put: "put", delete: "delete" };
/** AUTHOR_OPERATOR is the author of a write in the platform form. */
export const AUTHOR_OPERATOR = "operator";
/** slotOf is a secret's slot key, `<for>.<name>`. */
export function slotOf(s) {
    return `${s.for}.${s.name}`;
}
/** TARGET_IMAGES are the image names a required setting may target: every workload running it. */
export const TARGET_IMAGES = ["inference", "toolkit-mcp"];
/**
 * Client speaks the family for a parent's admin and for a workspace's own
 * admin, every request and reply sealed. The import fills the caller's slug.
 */
export class Client {
    nc;
    caller;
    carrier;
    timeout;
    constructor(nc, caller, opts = {}) {
        this.nc = nc;
        this.caller = caller;
        this.carrier = opts.carrier;
        this.timeout = opts.timeout ?? 5000;
    }
    async call(subj, body, opts, extra) {
        let hdrs;
        if (this.carrier !== undefined || extra !== undefined || opts.headers !== undefined) {
            hdrs = newHeaders();
            for (const src of [opts.headers, extra]) {
                if (src) {
                    for (const [k, values] of src) {
                        for (const v of values) {
                            hdrs.append(k, v);
                        }
                    }
                }
            }
            if (this.carrier !== undefined) {
                hdrs.set(CARRIER_HEADER, await this.carrier(audienceOf(subj)));
            }
        }
        const reply = await this.caller.request(this.nc, SERVICE, subj, body, {
            timeout: opts.timeout ?? this.timeout,
            headers: hdrs,
        });
        try {
            return JSON.parse(utf8Decode(reply.data));
        }
        catch (err) {
            throw new Error(`blueprint: ${subj}: reply is not JSON`, { cause: err });
        }
    }
    /**
     * set replaces a child's blueprint whole: the body is the document's text.
     * expected, when given, is the revision it was read at, and the write is
     * refused conflict when anything landed since.
     *
     * @deprecated The parent's set is retired (decision 0052): the service
     * refuses it; a parent assigns a named blueprint instead.
     */
    set(child, document, expected, opts = {}) {
        let extra;
        if (expected !== undefined) {
            extra = newHeaders();
            extra.set(EXPECT_REVISION_HEADER, String(expected));
        }
        return this.call(childSubject(child, Verb.set), utf8Encode(document), opts, extra);
    }
    /** get is a child's blueprint at its latest revision, or at a named one. */
    get(child, revision = 0, opts = {}) {
        const body = revision > 0 ? { revision } : {};
        return this.call(childSubject(child, Verb.get), utf8Encode(JSON.stringify(body)), opts);
    }
    /** history is the revisions the bucket holds, newest first, without their contents. */
    async history(child, opts = {}) {
        const r = await this.call(childSubject(child, Verb.history), new Uint8Array(0), opts);
        return r.entries ?? [];
    }
    /** delete removes a child's blueprint; the steward lowers everything it carried. */
    delete(child, opts = {}) {
        return this.call(childSubject(child, Verb.delete), new Uint8Array(0), opts);
    }
    /** status is a child's latest verified report; not-found where its steward has never reported. */
    status(child, opts = {}) {
        return this.call(childSubject(child, Verb.status), new Uint8Array(0), opts);
    }
    /** children is every child the relation gives the caller, each with its row. */
    async children(opts = {}) {
        const r = await this.call(childrenSubject(), new Uint8Array(0), opts);
        return r.children ?? [];
    }
    /**
     * assign makes a named blueprint the child's, with the child's sealed
     * secrets for its slots; the service writes the child's document resolved
     * from it. secrets absent keeps the child's current ones whose slot the
     * blueprint has. expected guards the child's own revision.
     */
    assign(child, name, secrets, expected, opts = {}) {
        const body = { blueprint: name };
        if (secrets !== undefined) {
            body.secrets = secrets;
        }
        return this.call(assignSubject(child), utf8Encode(JSON.stringify(body)), opts, expect(expected));
    }
    /** library is the caller's named blueprints. */
    async library(opts = {}) {
        const r = await this.call(libraryList(), new Uint8Array(0), opts);
        return r.blueprints ?? [];
    }
    /**
     * librarySet replaces a named blueprint whole; its secrets are slots.
     * expected is the revision it was read at. The reply's children is how many
     * children follow.
     */
    librarySet(name, document, expected, opts = {}) {
        return this.call(librarySubject(name, Verb.set), utf8Encode(document), opts, expect(expected));
    }
    /** libraryGet is a named blueprint at its latest revision, or a named one. */
    libraryGet(name, revision = 0, opts = {}) {
        const body = revision > 0 ? { revision } : {};
        return this.call(librarySubject(name, Verb.get), utf8Encode(JSON.stringify(body)), opts);
    }
    /** libraryHistory is a named blueprint's revisions, newest first, without their contents. */
    async libraryHistory(name, opts = {}) {
        const r = await this.call(librarySubject(name, Verb.history), new Uint8Array(0), opts);
        return r.entries ?? [];
    }
    /** libraryDelete removes a named blueprint; refused in-use while a child uses it. */
    libraryDelete(name, opts = {}) {
        return this.call(librarySubject(name, Verb.delete), new Uint8Array(0), opts);
    }
    /** ownGet is the workspace's own blueprint, read-only; not-found in a solo workspace. */
    ownGet(opts = {}) {
        return this.call(ownGet(), new Uint8Array(0), opts);
    }
}
function expect(expected) {
    if (expected === undefined) {
        return undefined;
    }
    const h = newHeaders();
    h.set(EXPECT_REVISION_HEADER, String(expected));
    return h;
}
// The compliance report (02-DESIGN/13-blueprints/02-convergence.md § the
// compliance report; 02-DESIGN/12-workspace-hierarchy/03-the-steward.md §
// the report and its verification): a compact Ed25519 JWS of its own type,
// signed by the steward's key in its workspace's vault, carrying the
// steward's attestation, which vouches that key. The page verifies it itself
// against the carrier keys it already holds, so that *attested* is the
// page's own verification and not the service's word.
/** REPORT_TYPE is the JWS type a report names. */
export const REPORT_TYPE = "impire-steward-report";
/** STEWARD_SIGNING_KEY is the vault path the steward signs under; the key id is `<path>@<version>`. */
export const STEWARD_SIGNING_KEY = "steward.signing-key";
/** The reference cadences: the report heartbeat and the factor its expiry is bounded by. */
export const REPORT_HEARTBEAT = 60_000;
export const REPORT_FACTOR = 3;
/** MAX_REPORT_LIFETIME is the longest lifetime a verifier accepts, in seconds. */
export const MAX_REPORT_LIFETIME = 3600;
/** The states of an item. */
export const ItemState = { applied: "applied", pending: "pending", failed: "failed" };
/** The kinds of drift. */
export const DriftKind = {
    suspended: "suspended",
    namesContested: "names-contested",
    overLimit: "over-limit",
    unapplied: "unapplied",
    undeliverable: "undeliverable",
    writtenBefore: "written-before",
    stewardBehind: "steward-behind",
    /** An installed solution whose license is past its expiry (02-DESIGN/17-plans/03-solution-licenses.md § the record). */
    licenseExpired: "license-expired",
};
/** ReportError is a report that does not verify, with the code of the step that failed. */
export class ReportError extends Error {
    code;
    constructor(code, message, options) {
        super(message, options);
        this.name = "ReportError";
        this.code = code;
    }
}
function invalid(message, cause) {
    return new ReportError(Code.reportInvalid, `blueprint: report invalid: ${message}`, { cause });
}
function split(token) {
    const [h, c, g, ...more] = token.split(".");
    if (h === undefined || c === undefined || g === undefined || more.length > 0) {
        throw invalid("not a compact JWS");
    }
    try {
        const hdr = JSON.parse(utf8Decode(base64UrlDecode(h)));
        return { hdr, claims: base64UrlDecode(c), signing: `${h}.${c}`, sig: base64UrlDecode(g) };
    }
    catch (err) {
        throw invalid("malformed", err);
    }
}
/** decodeReport reads a report without verifying it. */
export function decodeReport(token) {
    const { claims } = split(token);
    try {
        return JSON.parse(utf8Decode(claims));
    }
    catch (err) {
        throw invalid("malformed", err);
    }
}
/**
 * verifyReport checks a report offline against the carrier keys, in the
 * order impire-sdk-go/blueprint.VerifyReport does: its attestation verifies
 * and names the workspace, a workload among whose names is `steward`, and a
 * signing key; the report's signature verifies under that key; the report's
 * workspace, workload, placement and key version are the attestation's; it
 * outlives its issue by no more than MAX_REPORT_LIFETIME and has not
 * expired. now is in milliseconds. A failure throws a ReportError whose code
 * is `report-invalid`, `report-mismatch` or `report-expired`.
 */
export async function verifyReport(token, keys, workspace, now = Date.now()) {
    const { hdr, claims, signing, sig } = split(token);
    if (hdr.alg !== "EdDSA" || hdr.typ !== REPORT_TYPE) {
        throw invalid("not a steward's report");
    }
    let r;
    try {
        r = JSON.parse(utf8Decode(claims));
    }
    catch (err) {
        throw invalid("malformed", err);
    }
    if (r.typ !== REPORT_TYPE) {
        throw invalid("not a steward's report");
    }
    let a;
    try {
        a = await verifyAttestation(r.attestation, keys, now);
    }
    catch (err) {
        throw invalid("its attestation does not verify", err);
    }
    if (a.tenant !== workspace || !(a.names ?? []).includes("steward") || !a.signing_key) {
        throw invalid("its attestation does not vouch this workspace's steward");
    }
    let ok;
    try {
        const raw = base64UrlDecode(a.signing_key);
        const key = await crypto.subtle.importKey("raw", raw, { name: "Ed25519" }, false, [
            "verify",
        ]);
        ok = await crypto.subtle.verify({ name: "Ed25519" }, key, sig, utf8Encode(signing));
    }
    catch (err) {
        throw invalid("signature", err);
    }
    if (!ok) {
        throw invalid("signature");
    }
    const version = a.signing_key_version ?? 0;
    if (r.workspace !== a.tenant ||
        r.workload !== a.workload ||
        r.placement !== a.placement ||
        r.key_version !== version ||
        hdr.kid !== `${STEWARD_SIGNING_KEY}@${String(version)}`) {
        throw new ReportError(Code.reportMismatch, "blueprint: report mismatch: not the attested placement's");
    }
    if (!r.iat || !r.exp || r.exp - r.iat > MAX_REPORT_LIFETIME) {
        throw invalid("its lifetime");
    }
    if (Math.floor(now / 1000) >= r.exp) {
        throw new ReportError(Code.reportExpired, "blueprint: report expired");
    }
    return r;
}
/**
 * silent reports whether a child's steward has not been heard from for
 * longer than a report lives: the expiry of its last report, or — given only
 * when the report arrived — three heartbeats after it.
 */
export function silent(lastReport, received, now = Date.now()) {
    if (lastReport !== undefined && lastReport.exp > 0) {
        return Math.floor(now / 1000) >= lastReport.exp;
    }
    if (received === undefined || received === "") {
        return true;
    }
    return now - Date.parse(received) > REPORT_FACTOR * REPORT_HEARTBEAT;
}
/**
 * sealSecret seals a secret for a blueprint (01-the-blueprint.md § secrets):
 * the whole vault store request — the value, and no expected version — as
 * the vault's wire reads it, sealed with the platform's envelope toward the
 * child vault's transport key by a fresh ephemeral key, which rides beside it
 * as the sealer and is forgotten. The steward relays it verbatim; nothing but
 * that vault can open it.
 */
export function sealSecret(value, vaultKey) {
    if (!validPublicKey(vaultKey)) {
        throw new Error("blueprint: the vault key is not a curve public key");
    }
    const bytes = typeof value === "string" ? utf8Encode(value) : value;
    const req = { value: base64Encode(bytes) };
    const eph = Local.generate();
    try {
        const box = eph.seal(utf8Encode(JSON.stringify(req)), vaultKey);
        return { sealed: base64Encode(box), "sealed-by": eph.publicKey() };
    }
    finally {
        eph.wipe();
    }
}
/** documentText renders a document as the text the service stores: JSON, two-space indented. */
export function documentText(d) {
    return JSON.stringify(d, null, 2);
}
//# sourceMappingURL=blueprint.js.map